import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export const INTRO_SEEN_KEY =
  'wolf-forge-crm-intro-seen-v1'

export type AuthSession = {
  signedInAt: string
  expiresAt: string
}

type AuthContextValue = {
  authenticated: boolean
  checkingSession: boolean
  introPending: boolean
  session: AuthSession | null
  completeIntro: () => void
  login: (
    username: string,
    passcode: string,
  ) => Promise<boolean>
  logout: () => void
}

const AuthContext =
  createContext<AuthContextValue | null>(null)

function readSession(
  value: unknown,
): AuthSession | null {
  if (
    !value ||
    typeof value !== 'object'
  ) {
    return null
  }

  const session =
    value as Record<string, unknown>

  if (
    typeof session.signedInAt !== 'string' ||
    typeof session.expiresAt !== 'string'
  ) {
    return null
  }

  return {
    signedInAt: session.signedInAt,
    expiresAt: session.expiresAt,
  }
}

export function AuthProvider({
  children,
}: {
  children: ReactNode
}) {
  const [
    authenticated,
    setAuthenticated,
  ] = useState(false)

  const [
    checkingSession,
    setCheckingSession,
  ] = useState(true)

  const [
    introPending,
    setIntroPending,
  ] = useState(false)

  const [session, setSession] =
    useState<AuthSession | null>(null)

  const acceptLogin = useCallback(
    (nextSession: AuthSession | null) => {
      const shouldPlayIntro =
        sessionStorage.getItem(
          INTRO_SEEN_KEY,
        ) !== 'true'

      sessionStorage.setItem(
        INTRO_SEEN_KEY,
        'true',
      )

      setSession(nextSession)
      setIntroPending(shouldPlayIntro)
      setAuthenticated(true)
    },
    [],
  )

  const completeIntro =
    useCallback(
      () => setIntroPending(false),
      [],
    )

  useEffect(() => {
    const controller =
      new AbortController()

    fetch('/api/session', {
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        const contentType =
          response.headers.get(
            'content-type',
          ) ?? ''

        if (
          !response.ok ||
          !contentType.includes(
            'application/json',
          )
        ) {
          return {
            authenticated: false,
            session: null,
          }
        }

        const body =
          await response.json() as {
            authenticated?: unknown
            session?: unknown
          }

        return {
          authenticated:
            body.authenticated === true,
          session: readSession(body.session),
        }
      })
      .then((result) => {
        if (controller.signal.aborted) {
          return
        }

        if (!result.authenticated) {
          sessionStorage.removeItem(
            INTRO_SEEN_KEY,
          )
        }

        setSession(result.session)
        setAuthenticated(
          result.authenticated,
        )
      })
      .catch(() => {
        if (controller.signal.aborted) {
          return
        }

        sessionStorage.removeItem(
          INTRO_SEEN_KEY,
        )

        setSession(null)
        setAuthenticated(false)
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setCheckingSession(false)
        }
      })

    return () => controller.abort()
  }, [])

  const login = useCallback(
    async (
      username: string,
      passcode: string,
    ) => {
      try {
        const response = await fetch(
          '/api/login',
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'content-type':
                'application/json',
              accept: 'application/json',
            },
            body: JSON.stringify({
              username: username.trim(),
              passcode,
            }),
          },
        )

        const contentType =
          response.headers.get(
            'content-type',
          ) ?? ''

        if (
          !response.ok ||
          !contentType.includes(
            'application/json',
          )
        ) {
          return false
        }

        const body =
          await response.json() as {
            authenticated?: unknown
            session?: unknown
          }

        if (
          body.authenticated === true
        ) {
          acceptLogin(
            readSession(body.session),
          )

          return true
        }
      } catch {
        return false
      }

      return false
    },
    [acceptLogin],
  )

  const logout = useCallback(() => {
    void fetch('/api/session', {
      method: 'DELETE',
      credentials: 'include',
      keepalive: true,
    }).catch(() => undefined)

    sessionStorage.removeItem(
      INTRO_SEEN_KEY,
    )

    setSession(null)
    setIntroPending(false)
    setAuthenticated(false)
  }, [])

  const value = useMemo(
    () => ({
      authenticated,
      checkingSession,
      completeIntro,
      introPending,
      login,
      logout,
      session,
    }),
    [
      authenticated,
      checkingSession,
      completeIntro,
      introPending,
      login,
      logout,
      session,
    ],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error(
      'useAuth must be used inside AuthProvider',
    )
  }

  return context
}
